import { Block, TemplateData } from '../../types/blueprint'

interface BlockResponseItem {
  title: string // Question/block title
  response: unknown // User's response
  blockId?: string // Optional ID for reference
  blockType?: string // Optional type of block
}

interface BlockGroupResponse {
  name?: string
  blocks: BlockResponseItem[]
}

interface SectionResponse {
  name?: string
  blockGroups: BlockGroupResponse[]
}

export const getTemplateResponseSummary = (
  templateData: TemplateData
): SectionResponse[] => {
  const result: SectionResponse[] = []

  // Process each section, block group, and block to extract user answers
  templateData?.sections?.forEach((section) => {
    const sectionResponse: SectionResponse = {
      name: section.name,
      blockGroups: [],
    }

    section.blockGroups?.forEach((blockGroup) => {
      // Skip hidden groups
      if (blockGroup.hide) return

      const blockGroupResponse: BlockGroupResponse = {
        name: blockGroup.name,
        blocks: [],
      }

      blockGroup.blocks?.forEach((block) => {
        // Skip hidden blocks
        if (block.hide) return

        // Only process blocks that have a name (question title)
        if (!block.name) return

        const response = getBlockResponse(block)

        // Only add blocks that have actual responses
        if (response !== undefined && response !== null) {
          blockGroupResponse.blocks.push({
            title: block.name,
            response,
            blockId: block.id,
            blockType: block.type,
          })
        }
      })

      // Only add block groups that have blocks
      if (blockGroupResponse.blocks.length > 0) {
        sectionResponse.blockGroups.push(blockGroupResponse)
      }
    })

    // Only add sections that have block groups
    if (sectionResponse.blockGroups.length > 0) {
      result.push(sectionResponse)
    }
  })

  return result
}

// Helper function to extract the appropriate value based on block type
const getBlockResponse = (block: Block): unknown => {
  // Handle display blocks
  if (block.kind === 'display') {
    switch (block.type) {
      case 'text':
      case 'richText':
      case 'heading':
        return block.content
      case 'dateTime':
        return block.content
      case 'checklist':
        return block.checklist
          ?.filter((item) => item.checked)
          .map((item) => item.label)
      case 'select':
        return block.options
          ?.filter((option) => option.selected)
          .map((option) => option.label)
      case 'todo':
        return block.todos
          ?.filter((todo) => todo.complete)
          .map((todo) => todo.label)
      case 'weather':
        return block.value
      // Add more display block types as needed
      default:
        return undefined
    }
  }

  // Handle capture blocks
  if (block.kind === 'capture') {
    switch (block.type) {
      case 'text':
      case 'richText':
        return block.value
      case 'dateTime':
        return block.value
      case 'checkbox':
        return block.checkboxes
          ?.filter((checkbox) => checkbox.checked)
          .map((checkbox) => checkbox.label)
      case 'radio':
        return block.radioButtons?.find((radio) => radio.checked)?.label
      case 'select':
        return block.options
          ?.filter((option) => option.selected)
          .map((option) => option.label)
      case 'toggle':
        return block.value
      case 'signature':
        return block.signeeName
          ? { name: block.signeeName, time: block.time }
          : undefined
      case 'file':
        return block.fileList?.map((file) => file.name)
      case 'weather':
        return block.value
      case 'todo':
        return block.todos
          ?.filter((todo) => todo.complete)
          .map((todo) => todo.label)
      case 'timeNotes':
        return block.value
      case 'projectHoursBudget':
        return block.value
      case 'projectLocation':
        return block.projectLocationName || block.value
      case 'workerAttendance':
      case 'subcontractorSummary':
        return block.value
      // Add more capture block types as needed
      default:
        return undefined
    }
  }

  return undefined
}
